/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { MyOrganisationTabs } from 'src/components/Pages/MyOrganisation/MyOrganisationPage';
import { Order, Settings as TableSettings } from 'src/components/Table/types';
import { Columns } from 'src/components/UsersTable/types';
import { getOrgUsers } from 'src/services/apiService';
import { OrgUser } from 'src/types';

interface OrgUsersState {
  users: Array<OrgUser>;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  userTableSettings: TableSettings;
}
interface OrgUsersContextState extends OrgUsersState {
  updateUserTableSettings: (TableSettings) => void;
  updateSearchQuery: (string) => void;
}

const defaultOrgUsersContext = {
  users: [],
  isLoading: false,
  error: null,
  searchQuery: '',
  userTableSettings: {
    order: Order.Asc,
    orderBy: Columns.FullName,
    page: 0,
    rowsPerPage: 15,
    count: 0,
  },
  updateUserTableSettings: () => {
    throw new Error('<OrgUsersProvider> missing in a component tree');
  },
  updateSearchQuery: () => {
    throw new Error('<OrgUsersProvider> missing in a component tree');
  },
};

const OrgUsersContext = createContext<OrgUsersContextState>(
  defaultOrgUsersContext
);

const useOrgUsers = (): OrgUsersContextState => useContext(OrgUsersContext);

interface Props {
  orgId: string;
  children?: React.ReactNode;
}

export const OrgUsersProvider: React.FC<Props> = ({ orgId, children }) => {
  const location = useLocation();
  const routeMatch = useRouteMatch();
  const history = useHistory();

  const [users, setUsers] = useState(defaultOrgUsersContext.users);
  const [isLoading, setIsLoading] = useState(defaultOrgUsersContext.isLoading);
  const [error, setError] = useState(defaultOrgUsersContext.error);
  const [searchQuery, setSearchQuery] = useState(() => {
    const sessionTableSettings = JSON.parse(
      sessionStorage.getItem('tableSettings')
    );
    if (sessionTableSettings?.tab === MyOrganisationTabs.Users) {
      return sessionTableSettings.searchQuery;
    }
    return defaultOrgUsersContext.searchQuery;
  });

  const [userTableSettings, setUserTableSettings] = useState(() => {
    const sessionTableSettings = JSON.parse(
      sessionStorage.getItem('tableSettings')
    );
    if (sessionTableSettings?.tab === MyOrganisationTabs.Users) {
      return sessionTableSettings.tableSettings;
    }
    return defaultOrgUsersContext.userTableSettings;
  });

  const updateUserTableSettings = ({
    order,
    orderBy,
    page,
    rowsPerPage,
    count,
  }: TableSettings) => {
    setUserTableSettings((prevState) => ({
      order: order ?? prevState.order,
      orderBy: (orderBy as Columns) ?? prevState.orderBy,
      page: page ?? prevState.page,
      rowsPerPage: rowsPerPage ?? prevState.rowsPerPage,
      count: count ?? prevState.count,
    }));
  };

  const updateSearchQuery = (value) => {
    setSearchQuery((prevState) => {
      if (prevState !== value) {
        updateUserTableSettings({ ...prevState, page: 0 });
      }
      return value;
    });
  };

  const fetchUsers = (prevOrgId: string | null) => {
    let data: OrgUser[] = JSON.parse(sessionStorage.getItem('orgUsers'));
    setError(null);

    if (!data || prevOrgId !== orgId) {
      setIsLoading(true);
      sessionStorage.removeItem('orgUsers');
      setTimeout(async () => {
        try {
          data = await getOrgUsers(orgId);
          sessionStorage.setItem('orgUsers', JSON.stringify(data));
          setUsers(data);
        } catch (e) {
          setError(e.message);
        } finally {
          setIsLoading(false);
        }
      }, 1000);
    }
  };

  useEffect(() => {
    if (location.pathname === routeMatch.url) {
      const prevOrgId = sessionStorage.getItem('orgId');

      if (prevOrgId !== orgId || history.action === 'PUSH') {
        sessionStorage.removeItem('tableSettings');
        setUserTableSettings(defaultOrgUsersContext.userTableSettings);
      }

      fetchUsers(prevOrgId);
    }
    sessionStorage.setItem('orgId', orgId);
    return () => {
      setSearchQuery(defaultOrgUsersContext.searchQuery);
      setUsers(defaultOrgUsersContext.users);
      setIsLoading(defaultOrgUsersContext.isLoading);
      setError(defaultOrgUsersContext.error);
    };
  }, [orgId, location.pathname, routeMatch.url]);

  useEffect(() => {
    sessionStorage.setItem(
      'tableSettings',
      JSON.stringify({
        tab: MyOrganisationTabs.Users,
        tableSettings: userTableSettings,
        searchQuery,
      })
    );
  }, [userTableSettings, searchQuery]);

  return (
    <OrgUsersContext.Provider
      value={{
        users,
        isLoading,
        error,
        searchQuery,
        userTableSettings,
        updateSearchQuery,
        updateUserTableSettings,
      }}
    >
      {children}
    </OrgUsersContext.Provider>
  );
};

export default useOrgUsers;
